import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LayoutDashboard, CalendarClock, ArrowDownLeft, ArrowUpRight,
  Landmark, Tags, Users2, UserSquare, FileBarChart, ScrollText,
  Sun, Moon, LogOut, Wallet, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NAV, AUTH } from '@/constants/testIds';
import { roleLabel } from '@/lib/format';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const links = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: NAV.linkDashboard },
  { to: '/timeline', label: 'Timeline', icon: CalendarClock, testid: NAV.linkTimeline },
  { to: '/payable', label: 'Contas a Pagar', icon: ArrowUpRight, testid: NAV.linkPayable },
  { to: '/receivable', label: 'Contas a Receber', icon: ArrowDownLeft, testid: NAV.linkReceivable },
  { to: '/accounts', label: 'Contas Financeiras', icon: Landmark, testid: NAV.linkAccounts },
  { to: '/categories', label: 'Categorias', icon: Tags, testid: NAV.linkCategories },
  { to: '/parties', label: 'Clientes & Fornecedores', icon: UserSquare, testid: NAV.linkParties },
  { to: '/reports', label: 'Relatórios', icon: FileBarChart, testid: NAV.linkReports },
  { to: '/users', label: 'Usuários', icon: Users2, testid: NAV.linkUsers, perm: 'users.manage' },
  { to: '/audit', label: 'Auditoria', icon: ScrollText, testid: NAV.linkAudit, perm: 'audit.view' },
];

const PERMS_BY_ROLE = {
  SUPER_ADMIN: '*', ADMIN: ['users.manage', 'audit.view'],
  FINANCEIRO: [], GERENTE: [], OPERADOR: [], VISUALIZADOR: [],
};

function canSee(role, perm) {
  if (!perm) return true;
  const p = PERMS_BY_ROLE[role];
  if (p === '*') return true;
  return (p || []).includes(perm);
}

export default function AppLayout() {
  const { user, tenant, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav('/login', { replace: true }); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* SIDEBAR — fixa na viewport: não rola com o conteúdo (padrão global do sistema) */}
      <aside data-testid={NAV.sidebar} className="hidden md:flex w-64 shrink-0 flex-col border-r bg-card/40 backdrop-blur sticky top-0 h-screen">
        <div className="px-5 py-5 border-b shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-md shadow-emerald-500/20">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-semibold text-[15px] leading-tight">Ledgerly</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Gestão financeira</div>
            </div>
          </div>
          {tenant && (
            <div className="mt-4 rounded-md border bg-background/60 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Empresa</div>
              <div className="text-sm font-medium truncate">{tenant.name}</div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {links.filter(l => canSee(user?.role, l.perm)).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                  isActive ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent'
                }`
              }>
              <l.icon className="h-[18px] w-[18px]" />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t space-y-2 shrink-0 bg-card/40">
          <Button data-testid={NAV.themeToggle} variant="outline" size="sm" onClick={toggle} className="w-full justify-start gap-2">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={NAV.userMenu} className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-accent transition-colors">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 grid place-items-center text-white font-semibold text-sm">
                  {(user?.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400">{roleLabel[user?.role] || user?.role}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-mono text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={AUTH.logoutBtn} onClick={doLogout} className="text-rose-500 focus:text-rose-500">
                <LogOut className="mr-2 h-4 w-4" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* MOBILE topbar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 border-b bg-card/80 backdrop-blur flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-emerald-500 grid place-items-center"><Wallet className="h-4 w-4 text-white" /></div>
          <span className="font-heading font-semibold">Ledgerly</span>
        </div>
        <Button variant="ghost" size="icon" onClick={doLogout} data-testid={`${AUTH.logoutBtn}-mobile`}>
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        <div className="max-w-[1600px] mx-auto p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
